import { useState } from 'react';
import { DataStandard } from '@/lib/data-standards/schemas';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Download, Copy, Loader2, FileJson, FileCode, FileSpreadsheet, FileText, Package } from 'lucide-react';
import {
  downloadFile,
  exportFullPackage,
  exportJsonSchema,
  exportTypeScript,
  exportCsvFieldReference,
  exportOpenApiComponent,
  getFileExtension,
  getMimeType,
} from '@/lib/export';

interface ExportDialogProps {
  standard: DataStandard;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ExportFormat = 'full-package' | 'json-schema' | 'typescript' | 'csv' | 'openapi' | 'postman';

interface FormatOption {
  id: ExportFormat;
  label: string;
  description: string;
  icon: React.ElementType;
}

const FORMAT_OPTIONS: FormatOption[] = [
  {
    id: 'full-package',
    label: 'Full JSON Package',
    description: 'Complete schema with metadata, example, and field definitions',
    icon: Package,
  },
  {
    id: 'json-schema',
    label: 'JSON Schema Only',
    description: 'Validation schema for JSON validation libraries',
    icon: FileJson,
  },
  {
    id: 'typescript',
    label: 'TypeScript Definitions',
    description: 'Interface file (.d.ts) for TypeScript projects',
    icon: FileCode,
  },
  {
    id: 'csv',
    label: 'CSV Field Reference',
    description: 'Spreadsheet format for documentation',
    icon: FileSpreadsheet,
  },
  {
    id: 'openapi',
    label: 'OpenAPI Component',
    description: 'Drop-in component for OpenAPI 3.0 specs',
    icon: FileText,
  },
  {
    id: 'postman',
    label: 'Postman Collection',
    description: 'Ready-to-import Postman collection with CRUD endpoints',
    icon: FileJson,
  },
];

export function ExportDialog({ standard, open, onOpenChange }: ExportDialogProps) {
  const { toast } = useToast();
  const { trackExport } = useAnalytics();
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('full-package');
  const [isExporting, setIsExporting] = useState(false);

  const generateExport = async (): Promise<string> => {
    switch (selectedFormat) {
      case 'full-package':
        return exportFullPackage(standard);
      case 'json-schema':
        return exportJsonSchema(standard.schema, standard.title);
      case 'typescript':
        return exportTypeScript(
          standard.schema,
          standard.fields,
          standard.title,
          standard.version
        );
      case 'csv':
        return exportCsvFieldReference(standard.fields, standard.schema);
      case 'openapi':
        return exportOpenApiComponent(
          standard.schema,
          standard.title,
          standard.version,
          standard.description
        );
      case 'postman': {
        // Call edge function for Postman collection
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-postman`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              standardSlug: standard.id,
              baseUrl: 'https://api.example.com',
              includeValidateEndpoint: true,
            }),
          }
        );
        if (!response.ok) {
          throw new Error('Failed to generate Postman collection');
        }
        const collection = await response.json();
        return JSON.stringify(collection, null, 2);
      }
      default:
        return '';
    }
  };

  const handleDownload = async () => {
    setIsExporting(true);
    try {
      const content = await generateExport();
      const filename = `${standard.id}${getFileExtension(selectedFormat)}`;
      const mimeType = getMimeType(selectedFormat);
      downloadFile(content, filename, mimeType);
      trackExport(standard.id, selectedFormat);
      toast({ title: 'Export downloaded successfully' });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Export failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopy = async () => {
    setIsExporting(true);
    try {
      const content = await generateExport();
      await navigator.clipboard.writeText(content);
      toast({ title: 'Copied to clipboard' });
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Export {standard.title}</DialogTitle>
          <DialogDescription>
            Select an export format for this data standard.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh]">
          <RadioGroup
            value={selectedFormat}
            onValueChange={(value) => setSelectedFormat(value as ExportFormat)}
            className="space-y-3"
          >
            {FORMAT_OPTIONS.map((option) => (
              <div
                key={option.id}
                className="flex items-start space-x-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setSelectedFormat(option.id)}
              >
                <RadioGroupItem value={option.id} id={option.id} className="mt-1" />
                <div className="flex-1 space-y-1">
                  <Label
                    htmlFor={option.id}
                    className="flex items-center gap-2 cursor-pointer font-medium"
                  >
                    <option.icon className="h-4 w-4 text-muted-foreground" />
                    {option.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{option.description}</p>
                </div>
              </div>
            ))}
          </RadioGroup>
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleCopy} disabled={isExporting}>
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Copy className="h-4 w-4 mr-2" />
            )}
            Copy
          </Button>
          <Button onClick={handleDownload} disabled={isExporting}>
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
